import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper.js"

// innerHTML policy for this controller, audited in full:
//
// Assistant-authored text never reaches innerHTML. It is built as nodes in renderBotMessage,
// which is the one path here that carries untrusted content.
//
// Every other innerHTML write in this file is a compile-time constant: six are `= ""` clears, and
// ten are literal placeholder markup written here by hand -- the modal loading / empty / failed
// states, and the typing indicator's three dots. None of them interpolates a fetch response, a
// route parameter or user input, so none is an injection surface. Note there is no
// server-rendered-HTML case in this controller at all; every endpoint it calls returns JSON.
//
// If you add a write, it has to stay a constant. Anything carrying data belongs on a
// document.createElement path instead, next to renderBotMessage.
export default class extends Controller {
  static get targets() {
    return [
      "overlay", "drawer", "messages", "input",
      "designModal", "designGrid", "designSelection",
      "designPreviewImg", "designName",
      "collectionModal", "collectionGrid", "collectionModalTitle",
      "contactsModal", "contactsSearch", "contactsTableWrap",
      "contactsSelectedCount", "confirmContactsBtn",
      "recipientsSelection", "recipientsDetails",
      "paymentModal", "paymentList", "paymentSelection",
      "paymentDetails", "paymentForm", "paymentFormErrors"
    ]
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("click", this.handleDocumentClick)
    this.selectedDesignId = null
    this.designsCache = null
    this.selectedPaymentMethodId = null
    this.paymentMethodsCache = null
    this.selectedContactIds = new Set()
    // Selections are also persisted server-side so they survive a reload. When the user removes one, record it here so the next request tells the server
    // to drop it, otherwise the persisted value would silently come back
    this.pendingClears = new Set()
    this.contactsPage = 1
    this.contactsSortBy = "first_name"
    this.contactsSortDir = "asc"
    this.contactsSearchTerm = ""
    this.contactsSearchTimer = null
    this.loadHistory()
  }

  async loadHistory() {
    try {
      const response = await fetch("/chatbot", RequestHelper.defaultRequestOptions())
      if (!response.ok) {
        return
      }

      const data = await response.json()
      const messages = data.messages || []
      if (messages.length === 0) {
        return
      }

      this.messagesTarget.innerHTML = ""
      messages.forEach((message) => {
        const sender = message.role === "assistant" ? "bot" : "user"
        this.appendMessage(message.content, sender, { animate: false })
      })
      this.scrollToBottom()
    } catch (e) {
      // Leave the default greeting in place if history can't be loaded.
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("click", this.handleDocumentClick)
    if (this.typingTimer) {
      clearTimeout(this.typingTimer)
      this.typingTimer = null
    }
  }

  handleDocumentClick(event) {
    if (event.target.closest("#chat-drawer-toggle")) {
      this.toggle()
    }
  }

  toggle() {
    const overlay = this.overlayTarget
    const drawer = this.drawerTarget

    if (overlay.classList.contains("active")) {
      this.close()
    } else {
      overlay.classList.add("active")
      drawer.classList.remove("closed")
      this.setLaunchpadHidden(true)
      this.inputTarget.focus()
      this.scrollToBottom()
    }
  }

  close() {
    this.overlayTarget.classList.remove("active")
    this.drawerTarget.classList.add("closed")
    this.setLaunchpadHidden(false)
    this.closeDesignModal()
    this.closeCollectionModal()
    this.closeContactsModal()
    this.closePaymentModal()
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      if (this.contactsModalTarget.classList.contains("active")) {
        this.closeContactsModal()
      } else if (this.collectionModalTarget.classList.contains("active")) {
        this.closeCollectionModal()
      } else if (this.paymentModalTarget.classList.contains("active")) {
        this.closePaymentModal()
      } else if (this.designModalTarget.classList.contains("active")) {
        this.closeDesignModal()
      } else if (this.overlayTarget.classList.contains("active")) {
        this.close()
      }
    }
  }

  send(event) {
    if (event.key !== "Enter") {
      return
    }
    event.preventDefault()
    this.submitMessage()
  }

  sendClick(event) {
    event.preventDefault()
    this.submitMessage()
  }

  async submitMessage() {
    const input = this.inputTarget
    const message = input.value.trim()
    if (!message) {
      return
    }

    input.value = ""
    input.disabled = true

    this.appendMessage(message, "user")
    this.showTypingIndicator()

    try {
      const body = { message: message }
      if (this.selectedDesignId) {
        body.design_id = this.selectedDesignId
      }
      if (this.selectedPaymentMethodId) {
        body.payment_method_id = this.selectedPaymentMethodId
      }
      if (this.selectedContactIds.size > 0) {
        body.contact_ids = Array.from(this.selectedContactIds)
      }
      if (this.pendingClears.size > 0) {
        body.clear_selections = Array.from(this.pendingClears)
      }

      const response = await fetch("/chatbot", this.jsonPostOptions(body))

      this.removeTypingIndicator()

      if (!response.ok) {
        this.appendMessage("Sorry, something went wrong. Please try again.", "bot")
        return
      }

      // The server has applied any removals; don't resend them on the next turn
      this.pendingClears.clear()

      const data = await response.json()

      if (data.restart) {
        await this.appendMessage(data.message, "bot")
        this.appendRestartSummary(data.summary)
        return
      }

      await this.appendMessage(data.message, "bot")

      if (data.complete && data.action_type) {
        if (data.action_success) {
          const bulk = this.selectedContactIds.size > 1
          const verb = data.scheduled_for_later ? "scheduled" : "sent"
          const giftMsg = bulk ?
            `Gifts ${verb} ${data.scheduled_for_later ? "for" : "to"} ${this.selectedContactIds.size} recipients!` :
            `Gift ${verb} successfully!`
          const successMessages = {
            gift: giftMsg,
            contact: "Contact created successfully!"
          }
          this.appendStatus(successMessages[data.action_type] || "Action completed successfully!", "success")
          this.clearSelections()
        } else if (data.action_error) {
          const errorPrefixes = {
            gift: "Failed to send gift: ",
            contact: "Failed to create contact: "
          }
          const prefix = errorPrefixes[data.action_type] || "Error: "
          this.appendStatus(prefix + data.action_error, "error")
        }
      }
    } catch (error) {
      this.removeTypingIndicator()
      this.appendMessage("Sorry, something went wrong. Please try again.", "bot")
    } finally {
      input.disabled = false
      input.focus()
    }
  }

  clearSelections() {
    this.selectedDesignId = null
    this.designSelectionTarget.style.display = "none"
    this.selectedPaymentMethodId = null
    this.paymentSelectionTarget.style.display = "none"
    this.selectedContactIds = new Set()
    this.recipientsSelectionTarget.style.display = "none"
    this.pendingClears.clear()
  }

  async sendAutoMessage(message) {
    this.inputTarget.value = message
    await this.submitMessage()
  }

  async reset() {
    try {
      await fetch("/chatbot/reset", RequestHelper.defaultDeleteOptions())
    } catch (e) {
      // ignore
    }

    const messages = this.messagesTarget
    messages.innerHTML = ""
    this.appendGreeting()
    this.selectedDesignId = null
    this.designSelectionTarget.style.display = "none"
    this.selectedPaymentMethodId = null
    this.paymentMethodsCache = null
    this.paymentSelectionTarget.style.display = "none"
    this.selectedContactIds = new Set()
    this.recipientsSelectionTarget.style.display = "none"
    this.pendingClears.clear()
    this.inputTarget.focus()
  }

  // Design modal

  async openDesignModal() {
    this.designModalTarget.classList.add("active")
    await this.loadDesigns()
  }

  closeDesignModal() {
    this.designModalTarget.classList.remove("active")
  }

  async loadDesigns() {
    if (this.designsCache) {
      this.renderDesignGrid(this.designsCache)
      return
    }

    try {
      const response = await fetch("/chatbot/designs", RequestHelper.defaultRequestOptions())

      if (!response.ok) {
        throw new Error("Failed to load designs")
      }

      this.designsCache = await response.json()
      this.renderDesignGrid(this.designsCache)
    } catch (error) {
      this.designGridTarget.innerHTML =
        '<div class="chat-design-modal-loading">Failed to load designs. Please try again.</div>'
    }
  }

  renderDesignGrid(designs) {
    const grid = this.designGridTarget
    grid.innerHTML = ""

    const previews = document.createElement("div")
    previews.classList.add("chat-design-previews")

    designs.forEach((design) => {
      const item = document.createElement("div")
      item.classList.add("chat-design-item")
      if (design.id === this.selectedDesignId) {
        item.classList.add("selected")
      }

      const img = document.createElement("img")
      img.src = design.thumbnail_url
      img.alt = design.name
      img.loading = "lazy"

      const name = document.createElement("span")
      name.classList.add("chat-design-item-name")
      name.textContent = design.name

      item.appendChild(img)
      item.appendChild(name)
      item.addEventListener("click", () => this.selectDesign(design))
      previews.appendChild(item)
    })

    grid.appendChild(previews)
  }

  selectDesign(design) {
    this.selectedDesignId = design.id
    this.pendingClears.delete("design_id")
    this.designPreviewImgTarget.src = design.thumbnail_url
    this.designPreviewImgTarget.alt = design.name
    this.designNameTarget.textContent = design.name
    this.designSelectionTarget.style.display = "flex"
    this.closeDesignModal()
    this.sendAutoMessage(`I picked the "${design.name}" design`)
  }

  removeDesign() {
    this.selectedDesignId = null
    this.pendingClears.add("design_id")
    this.designSelectionTarget.style.display = "none"
  }

  // Payment modal

  async openPaymentModal() {
    this.paymentModalTarget.classList.add("active")
    this.showPaymentList()
    await this.loadPaymentMethods()
  }

  closePaymentModal() {
    this.paymentModalTarget.classList.remove("active")
  }

  async loadPaymentMethods() {
    if (this.paymentMethodsCache) {
      this.renderPaymentList(this.paymentMethodsCache)
      return
    }

    try {
      const response = await fetch("/chatbot/payment_methods", RequestHelper.defaultRequestOptions())

      if (!response.ok) {
        throw new Error("Failed to load payment methods")
      }

      this.paymentMethodsCache = await response.json()
      this.renderPaymentList(this.paymentMethodsCache)
    } catch (error) {
      this.paymentListTarget.innerHTML =
        '<div class="chat-payment-modal-loading">Failed to load payment methods. Please try again.</div>'
    }
  }

  renderPaymentList(data) {
    const list = this.paymentListTarget
    list.innerHTML = ""

    const allMethods = [
      ...data.organization.map(pm => ({ ...pm, ownerLabel: "Organization" })),
      ...data.user.map(pm => ({ ...pm, ownerLabel: "Personal" }))
    ]

    if (allMethods.length === 0) {
      list.innerHTML = '<div class="chat-payment-modal-loading">No payment methods on file.</div>'
    } else {
      allMethods.forEach(pm => {
        const item = document.createElement("div")
        item.classList.add("chat-payment-item")
        if (pm.id === this.selectedPaymentMethodId) {
          item.classList.add("selected")
        }

        const details = document.createElement("div")
        details.classList.add("chat-payment-item-details")

        const label = document.createElement("div")
        label.classList.add("chat-payment-item-label")
        const brandText = (pm.brand || "Card").charAt(0).toUpperCase() + (pm.brand || "Card").slice(1)
        label.textContent = `${brandText} •••• ${pm.last4}`
        if (pm.expiration) {
          label.textContent += ` (${pm.expiration})`
        }

        const owner = document.createElement("div")
        owner.classList.add("chat-payment-item-owner")
        owner.textContent = pm.ownerLabel

        details.appendChild(label)
        details.appendChild(owner)
        item.appendChild(details)
        item.addEventListener("click", () => this.selectPaymentMethod(pm))
        list.appendChild(item)
      })
    }

    const addBtn = document.createElement("button")
    addBtn.classList.add("btn-add-new-card")
    addBtn.textContent = "+ Add new card"
    addBtn.addEventListener("click", () => this.showPaymentForm())
    list.appendChild(addBtn)
  }

  selectPaymentMethod(pm) {
    this.selectedPaymentMethodId = pm.id
    this.pendingClears.delete("payment_method_id")
    const brandText = (pm.brand || "Card").charAt(0).toUpperCase() + (pm.brand || "Card").slice(1)
    let detailsText = `${brandText} •••• ${pm.last4}`
    if (pm.expiration) {
      detailsText += ` (${pm.expiration})`
    }
    this.paymentDetailsTarget.textContent = detailsText
    this.paymentSelectionTarget.style.display = "flex"
    this.closePaymentModal()
    this.autoConfirmPaymentSelection(detailsText)
  }

  autoConfirmPaymentSelection(detailsText) {
    this.inputTarget.value = `I've selected my payment method: ${detailsText}`
    this.submitMessage()
  }

  removePaymentMethod() {
    this.selectedPaymentMethodId = null
    this.pendingClears.add("payment_method_id")
    this.paymentSelectionTarget.style.display = "none"
  }

  showPaymentForm() {
    this.paymentListTarget.style.display = "none"
    this.paymentFormTarget.style.display = "block"
    this.paymentFormErrorsTarget.textContent = ""
    this.paymentFormTarget.querySelectorAll("input").forEach(input => { input.value = "" })
  }

  showPaymentList() {
    this.paymentListTarget.style.display = "block"
    this.paymentFormTarget.style.display = "none"
  }

  async submitPaymentForm(event) {
    event.preventDefault()
    const form = this.paymentFormTarget
    const name = form.querySelector('[data-field="name"]').value.trim()
    const number = form.querySelector('[data-field="number"]').value.trim()
    const exp = form.querySelector('[data-field="exp"]').value.trim()
    const cvc = form.querySelector('[data-field="cvc"]').value.trim()

    this.paymentFormErrorsTarget.textContent = ""

    if (!name || !number || !exp || !cvc) {
      this.paymentFormErrorsTarget.textContent = "Please fill in all fields."
      return
    }

    const expParts = exp.replace(/\s/g, "").split("/")
    if (expParts.length !== 2) {
      this.paymentFormErrorsTarget.textContent = "Invalid expiration format. Use MM / YY."
      return
    }

    const expMonth = parseInt(expParts[0], 10)
    const expYear = parseInt(expParts[1], 10)

    const submitBtn = form.querySelector(".btn-add-card")
    submitBtn.disabled = true
    submitBtn.textContent = "Adding..."

    try {
      const tokenResult = await new Promise((resolve, reject) => {
        Stripe.card.createToken({
          name: name,
          number: number,
          exp_month: expMonth,
          exp_year: expYear,
          cvc: cvc
        }, (status, response) => {
          if (response.error) {
            reject(new Error(response.error.message))
          } else {
            resolve(response)
          }
        })
      })

      const createResponse = await fetch("/account/payment_methods", this.jsonPostOptions({
        payment_method: {
          stripe_token: tokenResult.id,
          stripe_name: name,
          stripe_last4: tokenResult.card.last4,
          owner_type: "organization"
        }
      }))

      if (!createResponse.ok) {
        const errorData = await createResponse.json()
        throw new Error(errorData.message || "Failed to add card.")
      }

      const newCard = await createResponse.json()
      this.paymentMethodsCache = null

      this.selectPaymentMethod({
        id: newCard.id,
        brand: tokenResult.card.brand,
        last4: tokenResult.card.last4,
        name: name,
        type: "card",
        expiration: `${String(expMonth).padStart(2, "0")}/${String(expYear).padStart(2, "0")}`
      })
    } catch (error) {
      this.paymentFormErrorsTarget.textContent = error.message
    } finally {
      submitBtn.disabled = false
      submitBtn.textContent = "Add Card"
    }
  }

  appendPaymentPickerButton(messageDiv) {
    messageDiv.classList.add("has-payment-picker")
    const btn = document.createElement("button")
    btn.classList.add("btn", "btn-ghost", "btn-payment-picker-inline")
    btn.textContent = "Select payment method"
    btn.addEventListener("click", () => this.openPaymentModal())
    messageDiv.appendChild(btn)
    this.scrollToBottom()
  }

  appendCollectionPreviewButton(messageDiv, collectionName) {
    messageDiv.classList.add("has-collection-preview")
    const btn = document.createElement("button")
    btn.classList.add("btn", "btn-ghost", "btn-collection-preview-inline")
    btn.textContent = `Browse ${collectionName} collection`
    btn.addEventListener("click", () => this.openCollectionModal(collectionName))
    messageDiv.appendChild(btn)
    this.scrollToBottom()
  }

  async openCollectionModal(collectionName) {
    this.collectionModalTarget.classList.add("active")
    this.collectionModalTitleTarget.textContent = `${collectionName} Collection`
    this.collectionGridTarget.innerHTML = '<div class="chat-collection-modal-loading">Loading products...</div>'
    await this.loadCollectionProducts(collectionName)
  }

  closeCollectionModal() {
    this.collectionModalTarget.classList.remove("active")
  }

  async loadCollectionProducts(collectionName) {
    try {
      const response = await fetch(`/chatbot/collection_products?collection=${encodeURIComponent(collectionName)}`, RequestHelper.defaultRequestOptions())

      if (!response.ok) {
        throw new Error("Failed to load products")
      }

      const data = await response.json()
      this.renderCollectionGrid(data.products)
    } catch (error) {
      this.collectionGridTarget.innerHTML =
        '<div class="chat-collection-modal-loading">Failed to load products. Please try again.</div>'
    }
  }

  renderCollectionGrid(products) {
    const grid = this.collectionGridTarget
    grid.innerHTML = ""

    if (products.length === 0) {
      grid.innerHTML = '<div class="chat-collection-modal-loading">No products available in this collection.</div>'
      return
    }

    const list = document.createElement("div")
    list.classList.add("chat-collection-products")

    products.forEach((product) => {
      const item = document.createElement("div")
      item.classList.add("chat-collection-product")

      const img = document.createElement("img")
      img.src = product.image_url
      img.alt = product.name
      img.loading = "lazy"

      const info = document.createElement("div")
      info.classList.add("chat-collection-product-info")

      const name = document.createElement("span")
      name.classList.add("chat-collection-product-name")
      name.textContent = product.name

      info.appendChild(name)

      if (product.vendor) {
        const vendor = document.createElement("span")
        vendor.classList.add("chat-collection-product-vendor")
        vendor.textContent = product.vendor
        info.appendChild(vendor)
      }

      item.appendChild(img)
      item.appendChild(info)
      list.appendChild(item)
    })

    grid.appendChild(list)
  }

  // Contacts modal

  appendContactPickerButton(messageDiv) {
    messageDiv.classList.add("has-contact-picker")
    const btn = document.createElement("button")
    btn.classList.add("btn", "btn-ghost", "btn-contact-picker-inline")
    btn.textContent = "Select from contacts"
    btn.addEventListener("click", () => this.openContactsModal())
    messageDiv.appendChild(btn)
    this.scrollToBottom()
  }

  async openContactsModal() {
    this.contactsModalTarget.classList.add("active")
    this.contactsSearchTarget.value = this.contactsSearchTerm
    this.contactsPage = 1
    await this.loadContacts()
    this.contactsSearchTarget.focus()
  }

  closeContactsModal() {
    this.contactsModalTarget.classList.remove("active")
  }

  searchContacts() {
    if (this.contactsSearchTimer) {
      clearTimeout(this.contactsSearchTimer)
    }
    this.contactsSearchTimer = setTimeout(() => {
      this.contactsSearchTerm = this.contactsSearchTarget.value.trim()
      this.contactsPage = 1
      this.loadContacts()
    }, 300)
  }

  async loadContacts() {
    const params = new URLSearchParams({
      page: this.contactsPage,
      per_page: 25,
      sort_by: this.contactsSortBy,
      sort_dir: this.contactsSortDir
    })
    if (this.contactsSearchTerm) {
      params.set("search", this.contactsSearchTerm)
    }

    try {
      const response = await fetch(`/chatbot/contacts?${params}`, RequestHelper.defaultRequestOptions())
      if (!response.ok) {
        throw new Error("Failed to load contacts")
      }

      const data = await response.json()
      this.renderContactsTable(data)
    } catch (error) {
      this.contactsTableWrapTarget.innerHTML =
        '<div class="chat-contacts-modal-loading">Failed to load contacts. Please try again.</div>'
    }
  }

  renderContactsTable(data) {
    const wrap = this.contactsTableWrapTarget
    wrap.innerHTML = ""

    if (data.contacts.length === 0) {
      wrap.innerHTML = '<div class="chat-contacts-modal-loading">No contacts found.</div>'
      return
    }

    const table = document.createElement("table")
    table.classList.add("chat-contacts-table")

    // Header
    const thead = document.createElement("thead")
    const headerRow = document.createElement("tr")

    const thCheck = document.createElement("th")
    thCheck.classList.add("col-check")
    const selectAllCheck = document.createElement("input")
    selectAllCheck.type = "checkbox"
    selectAllCheck.addEventListener("change", (e) => this.toggleSelectAllContacts(e, data.contacts))
    // Check if all on this page are selected
    const allOnPageSelected = data.contacts.every(c => this.selectedContactIds.has(c.id))
    selectAllCheck.checked = allOnPageSelected && data.contacts.length > 0
    thCheck.appendChild(selectAllCheck)
    headerRow.appendChild(thCheck)

    const columns = [
      { key: "first_name", label: "First Name" },
      { key: "last_name", label: "Last Name" },
      { key: "email", label: "Email" }
    ]

    columns.forEach(col => {
      const th = document.createElement("th")
      th.textContent = col.label
      th.classList.add("sortable")
      if (this.contactsSortBy === col.key) {
        th.classList.add(this.contactsSortDir === "asc" ? "sort-asc" : "sort-desc")
      }
      th.addEventListener("click", () => this.sortContacts(col.key))
      headerRow.appendChild(th)
    })

    thead.appendChild(headerRow)
    table.appendChild(thead)

    // Body
    const tbody = document.createElement("tbody")
    data.contacts.forEach(contact => {
      const row = document.createElement("tr")
      row.classList.add("chat-contact-row")
      if (this.selectedContactIds.has(contact.id)) {
        row.classList.add("selected")
      }

      const tdCheck = document.createElement("td")
      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.checked = this.selectedContactIds.has(contact.id)
      checkbox.addEventListener("change", () => this.toggleContact(contact, row, checkbox))
      tdCheck.appendChild(checkbox)
      row.appendChild(tdCheck)

      const tdFirst = document.createElement("td")
      tdFirst.textContent = contact.first_name || ""
      row.appendChild(tdFirst)

      const tdLast = document.createElement("td")
      tdLast.textContent = contact.last_name || ""
      row.appendChild(tdLast)

      const tdEmail = document.createElement("td")
      tdEmail.textContent = contact.email || ""
      tdEmail.classList.add("col-email")
      row.appendChild(tdEmail)

      row.addEventListener("click", (e) => {
        if (e.target.tagName !== "INPUT") {
          checkbox.checked = !checkbox.checked
          this.toggleContact(contact, row, checkbox)
        }
      })

      tbody.appendChild(row)
    })
    table.appendChild(tbody)
    wrap.appendChild(table)

    // Pagination
    if (data.total_pages > 1) {
      const pagination = document.createElement("div")
      pagination.classList.add("chat-contacts-pagination")

      const prevBtn = document.createElement("button")
      prevBtn.classList.add("btn", "btn-ghost", "btn-sm")
      prevBtn.textContent = "Previous"
      prevBtn.disabled = data.page <= 1
      prevBtn.addEventListener("click", () => {
        this.contactsPage = data.page - 1
        this.loadContacts()
      })

      const pageInfo = document.createElement("span")
      pageInfo.classList.add("chat-contacts-page-info")
      pageInfo.textContent = `Page ${data.page} of ${data.total_pages}`

      const nextBtn = document.createElement("button")
      nextBtn.classList.add("btn", "btn-ghost", "btn-sm")
      nextBtn.textContent = "Next"
      nextBtn.disabled = data.page >= data.total_pages
      nextBtn.addEventListener("click", () => {
        this.contactsPage = data.page + 1
        this.loadContacts()
      })

      pagination.appendChild(prevBtn)
      pagination.appendChild(pageInfo)
      pagination.appendChild(nextBtn)
      wrap.appendChild(pagination)
    }
  }

  toggleContact(contact, row, checkbox) {
    if (checkbox.checked) {
      this.selectedContactIds.add(contact.id)
      row.classList.add("selected")
    } else {
      this.selectedContactIds.delete(contact.id)
      row.classList.remove("selected")
    }
    this.updateContactsSelectedCount()
  }

  toggleSelectAllContacts(event, contacts) {
    const checked = event.target.checked
    contacts.forEach(contact => {
      if (checked) {
        this.selectedContactIds.add(contact.id)
      } else {
        this.selectedContactIds.delete(contact.id)
      }
    })
    this.updateContactsSelectedCount()
    this.loadContacts()
  }

  sortContacts(column) {
    if (this.contactsSortBy === column) {
      this.contactsSortDir = this.contactsSortDir === "asc" ? "desc" : "asc"
    } else {
      this.contactsSortBy = column
      this.contactsSortDir = "asc"
    }
    this.contactsPage = 1
    this.loadContacts()
  }

  updateContactsSelectedCount() {
    const count = this.selectedContactIds.size
    this.contactsSelectedCountTarget.textContent = `${count} selected`
    this.confirmContactsBtnTarget.disabled = count === 0
  }

  confirmContacts() {
    const count = this.selectedContactIds.size
    if (count === 0) {
      return
    }

    this.pendingClears.delete("contact_ids")
    this.recipientsDetailsTarget.textContent = `${count} recipient${count === 1 ? "" : "s"} selected`
    this.recipientsSelectionTarget.style.display = "flex"
    this.closeContactsModal()

    this.inputTarget.value = `I've selected ${count} recipient${count === 1 ? "" : "s"} from my contacts`
    this.submitMessage()
  }

  removeRecipients() {
    this.selectedContactIds = new Set()
    this.pendingClears.add("contact_ids")
    this.recipientsSelectionTarget.style.display = "none"
  }

  appendGreeting() {
    const div = document.createElement("div")
    div.classList.add("chat-message", "chat-message--bot")

    const bubble = document.createElement("div")
    bubble.classList.add("chat-bubble")
    bubble.textContent = "Hi! I can help you send a gift. Who would you like to send to? Just tell me their name and email."

    div.appendChild(bubble)

    // Contact picker entry point temporarily disabled.
    // const btn = document.createElement("button")
    // btn.classList.add("btn", "btn-ghost", "btn-contact-picker-inline")
    // btn.textContent = "Select from contacts"
    // btn.addEventListener("click", () => this.openContactsModal())
    // div.appendChild(btn)

    this.messagesTarget.appendChild(div)
  }

  appendMessage(text, sender, { animate = true } = {}) {
    const div = document.createElement("div")
    div.classList.add("chat-message", `chat-message--${sender}`)

    const bubble = document.createElement("div")
    bubble.classList.add("chat-bubble")

    div.appendChild(bubble)
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()

    if (sender === "bot") {
      const hasDesignPicker = text.includes("[DESIGN_PICKER]")
      const hasPaymentPicker = text.includes("[PAYMENT_PICKER]")
      // const hasContactPicker = text.includes("[CONTACT_PICKER]")
      const hasGiftPreview = text.includes("[GIFT_PREVIEW]")
      const pricePickerMatch = text.match(/\[PRICE_PICKER:(domestic|international)\]/)
      const hasPricePicker = !!pricePickerMatch
      const pricePickerInternational = pricePickerMatch ? pricePickerMatch[1] === "international" : false
      const collectionMatches = [...text.matchAll(/\[COLLECTION_PREVIEW:([^\]]+)\]/g)]
      const collectionNames = [...new Set(collectionMatches.map(m => m[1].trim()))]
      const cleanText = this.stripUiMarkers(text)

      if (!animate) {
        this.renderBotMessage(bubble, cleanText)
        this.scrollToBottom()
        return Promise.resolve()
      }

      const renderText = cleanText.length > 300 ?
        Promise.resolve().then(() => { this.renderBotMessage(bubble, cleanText) }) :
        this.typeText(bubble, cleanText)

      return renderText.then(() => {
        if (hasDesignPicker) {
          this.appendDesignPickerButton(div)
        }
        if (hasPaymentPicker) {
          this.appendPaymentPickerButton(div)
        }
        // if (hasContactPicker) {
        //   this.appendContactPickerButton(div)
        // }
        if (hasGiftPreview) {
          this.appendGiftPreviewButtons(div)
        }
        if (hasPricePicker) {
          this.appendPricePickerButtons(div, pricePickerInternational)
        }
        collectionNames.forEach(name => {
          this.appendCollectionPreviewButton(div, name)
        })
        this.scrollToBottom()
      })
    } else {
      bubble.textContent = text
      return Promise.resolve()
    }
  }

  async appendPricePickerButtons(messageDiv, international) {
    messageDiv.classList.add("has-price-picker")
    try {
      const response = await fetch(`/chatbot/price_points?international=${international}`, RequestHelper.defaultRequestOptions())
      const pricePoints = await response.json()

      pricePoints.forEach(point => {
        const btn = document.createElement("button")
        btn.classList.add("btn", "btn-ghost", "btn-price-picker-inline")
        btn.textContent = point.price
        btn.addEventListener("click", () => {
          if (point.count === 1) {
            this.sendAutoMessage(`I'd like the ${point.price} option (${point.collections[0].name})`)
          } else {
            this.sendAutoMessage(`I'd like the ${point.price} option`)
          }
        })
        messageDiv.appendChild(btn)
      })
      this.scrollToBottom()
    } catch (e) {
      // fall back to letting user type
    }
  }

  appendDesignPickerButton(messageDiv) {
    messageDiv.classList.add("has-design-picker")
    const btn = document.createElement("button")
    btn.classList.add("btn", "btn-ghost", "btn-design-picker-inline")
    btn.textContent = "Browse designs"
    btn.addEventListener("click", () => this.openDesignModal())
    messageDiv.appendChild(btn)
    this.scrollToBottom()
  }

  appendGiftPreviewButtons(messageDiv) {
    messageDiv.classList.add("has-gift-preview")

    const emailBtn = document.createElement("button")
    emailBtn.classList.add("btn", "btn-ghost", "btn-gift-preview-inline")
    emailBtn.textContent = "Preview email"
    emailBtn.addEventListener("click", () => this.openPreviewWindow("/chatbot/email_preview"))
    messageDiv.appendChild(emailBtn)

    const redemptionBtn = document.createElement("button")
    redemptionBtn.classList.add("btn", "btn-ghost", "btn-gift-preview-inline")
    redemptionBtn.textContent = "Preview redemption"
    redemptionBtn.addEventListener("click", () => this.openPreviewWindow("/chatbot/redemption_preview"))
    messageDiv.appendChild(redemptionBtn)

    this.scrollToBottom()
  }

  async openPreviewWindow(path) {
    const params = new URLSearchParams()
    if (this.selectedDesignId) {
      params.set("design_id", this.selectedDesignId)
    }
    this.selectedContactIds.forEach(id => params.append("contact_ids[]", id))

    const previewWindow = window.open("", "gift_preview", "width=700,height=800,scrollbars=yes")
    previewWindow.document.write("<p>Loading preview...</p>")

    try {
      const response = await fetch(`${path}?${params.toString()}`, RequestHelper.defaultRequestOptions())

      const html = response.ok ?
        await response.text() :
        "<p>Failed to load preview.</p>"

      if (response.ok) {
        const disableScript = `<script>document.addEventListener("click",function(e){var a=e.target.closest("a");if(a){e.preventDefault();e.stopPropagation();return false}},true)</script>`
        previewWindow.document.open()
        previewWindow.document.write(disableScript + html)
        previewWindow.document.close()
      } else {
        previewWindow.document.open()
        previewWindow.document.write(html)
        previewWindow.document.close()
      }
    } catch (e) {
      previewWindow.document.open()
      previewWindow.document.write("<p>Failed to load preview.</p>")
      previewWindow.document.close()
    }
  }

  typeText(element, text) {
    return new Promise((resolve) => {
      let index = 0
      const tick = () => {
        if (index <= text.length) {
          this.renderBotMessage(element, text.slice(0, index))
          this.scrollToBottom()
          index++
          this.typingTimer = setTimeout(tick, 15)
        } else {
          this.typingTimer = null
          resolve()
        }
      }
      tick()
    })
  }

  // Mirrors AI::Chat::HistoryPresenter#strip_ui_markers. Only horizontal
  // whitespace may be consumed around a marker — eating the newlines between
  // "- **Name** [COLLECTION_PREVIEW:Name]" list items runs the names together
  // (LT-2288). A line that is nothing but a marker (optionally as a "- " list
  // item) is dropped whole.
  stripUiMarkers(text) {
    const marker = "\\[(?:DESIGN_PICKER|PAYMENT_PICKER|CONTACT_PICKER|GIFT_PREVIEW|PRICE_PICKER:(?:domestic|international)|COLLECTION_PREVIEW:[^\\]]+)\\]"
    return text
      .replace(new RegExp(`^[^\\S\\n]*(?:-[^\\S\\n]*)?${marker}[^\\S\\n]*(?:\\n|$)`, "gm"), "")
      .replace(new RegExp(`[^\\S\\n]*${marker}[^\\S\\n]*`, "g"), " ")
      .replace(/[^\S\n]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  }

  // Bot text is written by the model, so it is untrusted and is never turned into an HTML string.
  // Building real nodes makes the escaping structural rather than procedural: there is no parse
  // step left for markup in the text to be picked up by, so an edit here cannot reintroduce the
  // injection by forgetting to escape first. This replaced an escapeHtml-then-interpolate
  // formatter whose output was assigned to innerHTML -- correct as written, but one careless
  // edit away from an XSS, and the assistant's output is the least trustworthy input we render.
  renderBotMessage(element, text) {
    element.textContent = ""
    element.appendChild(this.botMessageFragment(text))
  }

  // The assistant emits a small subset of markdown: **emphasis**, which never spans a line, and
  // hard newlines. Splitting on the same non-greedy, single-line pattern the string formatter
  // used keeps the rendering identical -- odd indices hold the captured emphasis, even indices
  // the plain runs between them.
  botMessageFragment(text) {
    const fragment = document.createDocumentFragment()

    text.split(/\*\*(.+?)\*\*/).forEach((part, index) => {
      if (index % 2 === 1) {
        const strong = document.createElement("strong")
        this.appendTextWithBreaks(strong, part)
        fragment.appendChild(strong)
      } else {
        this.appendTextWithBreaks(fragment, part)
      }
    })

    return fragment
  }

  // One <br> per newline, matching what the "\n" -> "<br>" replacement produced.
  appendTextWithBreaks(parent, text) {
    text.split("\n").forEach((line, index) => {
      if (index > 0) {
        parent.appendChild(document.createElement("br"))
      }
      if (line) {
        parent.appendChild(document.createTextNode(line))
      }
    })
  }

  appendRestartSummary(summary) {
    if (!summary) {
      return
    }

    const div = document.createElement("div")
    div.classList.add("chat-message", "chat-message--bot", "chat-message--restart")

    const block = document.createElement("div")
    block.classList.add("chat-restart-summary")
    block.textContent = summary
    div.appendChild(block)

    const freshBtn = document.createElement("button")
    freshBtn.classList.add("btn", "btn-ghost", "btn-restart-fresh-inline")
    freshBtn.textContent = "Start fresh & continue"
    freshBtn.addEventListener("click", () => this.startFresh(summary))
    div.appendChild(freshBtn)

    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  // The server has already cleared the session, so we just reset the UI and pre-fill the
  // recap so the user can review it and send to begin a fresh conversation.
  startFresh(summary) {
    this.messagesTarget.innerHTML = ""
    this.appendGreeting()
    this.clearSelections()
    this.paymentMethodsCache = null
    this.inputTarget.value = summary
    this.inputTarget.focus()
    this.scrollToBottom()
  }

  appendStatus(text, type) {
    const div = document.createElement("div")
    div.classList.add("chat-message", "chat-message--status", `chat-message--${type}`)

    const bubble = document.createElement("div")
    bubble.classList.add("chat-bubble", `chat-bubble--${type}`)
    bubble.textContent = text

    div.appendChild(bubble)
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  showTypingIndicator() {
    const div = document.createElement("div")
    div.classList.add("chat-message", "chat-message--bot", "chat-typing")

    const bubble = document.createElement("div")
    bubble.classList.add("chat-bubble", "typing-indicator")
    bubble.innerHTML = "<span></span><span></span><span></span>"

    div.appendChild(bubble)
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  removeTypingIndicator() {
    const typing = this.messagesTarget.querySelector(".chat-typing")
    if (typing) {
      typing.remove()
    }
  }

  scrollToBottom() {
    const messages = this.messagesTarget
    messages.scrollTop = messages.scrollHeight
  }

  setLaunchpadHidden(hidden) {
    if (hidden) {
      this.hideLaunchpadHost()
      this.launchpadObserver = new MutationObserver(() => this.hideLaunchpadHost())
      this.launchpadObserver.observe(document.body, { childList: true, subtree: true })
    } else if (this.launchpadObserver) {
      this.launchpadObserver.disconnect()
      this.launchpadObserver = null
      if (this.launchpadHost) {
        this.launchpadHost.style.removeProperty("display")
        this.launchpadHost = null
      }
    }
  }

  hideLaunchpadHost() {
    if (this.launchpadHost) {
      if (this.launchpadHost.style.display !== "none") {
        this.launchpadHost.style.setProperty("display", "none", "important")
      }
      return
    }

    const shadowHosts = document.body.querySelectorAll("*")
    for (const el of shadowHosts) {
      if (el.shadowRoot && el.shadowRoot.getElementById("launchpad-button-wrapper")) {
        this.launchpadHost = el
        el.style.setProperty("display", "none", "important")
        return
      }
    }
  }

  jsonPostOptions(body) {
    const options = RequestHelper.defaultPostOptions()
    options.headers["Content-Type"] = "application/json"
    options.body = JSON.stringify(body)
    return options
  }
}
